import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";
import { fireEvent } from "@/lib/events";
import { getLanguageCode } from "@/lib/locale";

interface OnboardingSlidesProps {
  userId: string;
  locale: string;
  onComplete: () => void;
}

interface Slide {
  emoji?: string;
  video?: string;
  title: string;
  body: string;
  cta?: string;
}

const SWIPE_THRESHOLD = 50;

const OnboardingSlides = ({ userId, locale, onComplete }: OnboardingSlidesProps) => {
  const { t } = useTranslation();

  const SLIDES: Slide[] = [
    {
      video: "/mori-intro.mp4",
      title: t("onboarding.slide1.title"),
      body: t("onboarding.slide1.body"),
    },
    {
      emoji: t("onboarding.slide2.emoji"),
      title: t("onboarding.slide2.title"),
      body: t("onboarding.slide2.body"),
    },
    {
      emoji: t("onboarding.slide3.emoji"),
      title: t("onboarding.slide3.title"),
      body: t("onboarding.slide3.body"),
    },
    {
      emoji: t("onboarding.slide4.emoji"),
      title: t("onboarding.slide4.title"),
      body: t("onboarding.slide4.body"),
    },
    {
      emoji: t("onboarding.slide5.emoji"),
      title: t("onboarding.slide5.title"),
      body: t("onboarding.slide5.body"),
    },
    {
      emoji: t("onboarding.slide6.emoji"),
      title: t("onboarding.slide6.title"),
      body: t("onboarding.slide6.body"),
      cta: t("onboarding.slide6.cta"),
    },
  ];

  const [index, setIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const dragStartX = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const updateWidth = () => {
      setContainerWidth(containerRef.current?.offsetWidth ?? window.innerWidth);
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // Pause video when user swipes away from slide 1
  useEffect(() => {
    if (index !== 0 && videoRef.current) {
      videoRef.current.pause();
      setIsVideoPlaying(false);
    }
  }, [index]);

  const finish = async () => {
    const { error } = await supabase
      .from("profiles")
      .update({ first_open_completed: true })
      .eq("id", userId);
    if (error) {
      console.error("[OnboardingSlides] failed to persist first_open_completed", error);
      onComplete();
      return;
    }
    fireEvent("app.first.open", {
      userId,
      locale,
      detectedLanguage: getLanguageCode(locale),
    });
    fireEvent("intro.completed", { userId });
    onComplete();
  };

  const goNext = () => setIndex((i) => Math.min(i + 1, SLIDES.length - 1));
  const goPrev = () => setIndex((i) => Math.max(i - 1, 0));

  const handlePointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest("button")) return;
    if ((e.target as HTMLElement).closest("[data-video-container]")) return;
    dragStartX.current = e.clientX;
    setDragging(true);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (dragStartX.current === null) return;
    const raw = e.clientX - dragStartX.current;
    let delta = raw;
    if (index === 0 && raw > 0) delta = raw * 0.35;
    if (index === SLIDES.length - 1 && raw < 0) delta = raw * 0.35;
    setDragOffset(delta);
  };

  const handlePointerEnd = (e: React.PointerEvent) => {
    if (dragStartX.current === null) return;
    const delta = e.clientX - dragStartX.current;
    dragStartX.current = null;
    setDragging(false);
    setDragOffset(0);
    if (delta > SWIPE_THRESHOLD) goPrev();
    else if (delta < -SWIPE_THRESHOLD) goNext();
  };

  const handleVideoPlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsVideoPlaying(true);
    }
  };

  const handleVideoPause = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsVideoPlaying(false);
    }
  };

  const isLast = index === SLIDES.length - 1;

  const dragPct = containerWidth > 0 ? (dragOffset / containerWidth) * 100 : 0;
  const trackTranslate = `translateX(calc(-${index * 100}% + ${dragPct}%))`;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[100] flex flex-col bg-background overflow-hidden select-none"
      style={{ touchAction: "none" }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
    >
      {/* Sliding track */}
      <div
        className="flex h-full w-full"
        style={{
          transform: trackTranslate,
          transition: dragging ? "none" : "transform 250ms ease-out",
          willChange: "transform",
        }}
      >
        {SLIDES.map((slide, i) => (
          <div
            key={i}
            className="shrink-0 w-full h-full flex flex-col items-center justify-center px-6"
          >
            {slide.video ? (
              /* Video slide — sized by height so full portrait width is visible */
              <div
                data-video-container
                style={{
                  position: "relative",
                  display: "inline-block",
                  borderRadius: 16,
                  overflow: "hidden",
                  backgroundColor: "#1a1a1a",
                  touchAction: "auto",
                }}
              >
                <video
                  ref={videoRef}
                  src={slide.video}
                  playsInline
                  preload="metadata"
                  onEnded={() => setIsVideoPlaying(false)}
                  style={{
                    display: "block",
                    height: "42vh",
                    width: "auto",
                    maxWidth: "85vw",
                  }}
                />
                {/* Play overlay */}
                {!isVideoPlaying && (
                  <button
                    type="button"
                    onClick={handleVideoPlay}
                    aria-label="Play Mori's introduction"
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(0,0,0,0.25)",
                      border: "none",
                      cursor: "pointer",
                      width: "100%",
                    }}
                  >
                    <span
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        backgroundColor: "rgba(255,255,255,0.92)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 22,
                        paddingLeft: 4,
                      }}
                    >
                      ▶
                    </span>
                  </button>
                )}
                {/* Pause button — visible while playing */}
                {isVideoPlaying && (
                  <button
                    type="button"
                    onClick={handleVideoPause}
                    aria-label="Pause"
                    style={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      backgroundColor: "rgba(0,0,0,0.45)",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: 14,
                    }}
                  >
                    ⏸
                  </button>
                )}
              </div>
            ) : (
              <div aria-hidden style={{ fontSize: 52, lineHeight: 1 }}>
                {slide.emoji}
              </div>
            )}
            <h1
              className="text-center text-foreground"
              style={{
                fontSize: 22,
                fontWeight: 500,
                marginTop: 16,
                maxWidth: 320,
              }}
            >
              {slide.title}
            </h1>
            <p
              className="text-center text-muted-foreground"
              style={{
                fontSize: 15,
                lineHeight: 1.7,
                maxWidth: 280,
                marginTop: 12,
              }}
            >
              {slide.body}
            </p>
          </div>
        ))}
      </div>

      {/* Dots + CTA / swipe hint */}
      <div className="absolute bottom-10 left-0 right-0 flex flex-col items-center gap-5 px-6 pointer-events-none">
        <div className="flex items-center gap-2">
          {SLIDES.map((_, i) => (
            <span
              key={i}
              aria-hidden
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: i === index ? "#7A9A7C" : "transparent",
                border: i === index ? "none" : "0.5px solid rgba(122, 154, 124, 0.35)",
                transition: "background-color 200ms ease-out",
              }}
            />
          ))}
        </div>

        {isLast ? (
          <button
            type="button"
            onClick={finish}
            className="pointer-events-auto transition-transform active:scale-95"
            style={{
              backgroundColor: "#7A9A7C",
              color: "white",
              borderRadius: 12,
              padding: "12px 24px",
              fontSize: 15,
              fontWeight: 500,
              border: "none",
              cursor: "pointer",
            }}
          >
            {SLIDES[SLIDES.length - 1].cta}
          </button>
        ) : (
          <p style={{ fontSize: 12, color: "rgba(120, 100, 80, 0.7)" }}>
            {t("onboarding.swipeToContinue")}
          </p>
        )}
      </div>
    </div>
  );
};

export default OnboardingSlides;
